import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Lightbulb } from 'lucide-react';
import HubLayout from '../../components/hub/HubLayout.jsx';
import { AI } from '../../lib/ai.js';

const cardKey = (deckId, idx) => `${deckId}:${idx}`;

function loadJSON(key) {
  try { const raw = localStorage.getItem(key); return raw ? JSON.parse(raw) : null; } catch { return null; }
}
function saveJSON(key, val) {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch { /* ignore */ }
}

export default function Flashcards({ config }) {
  const PROGRESS_KEY = `${config.storagePrefix}.flash.progress`;
  const aiCacheKey = useCallback(
    (kind, deckId, idx) => `${config.storagePrefix}.ai.flash.${kind}.${cardKey(deckId, idx)}`,
    [config.storagePrefix]
  );

  const loadProgress = useCallback(() => {
    try { return JSON.parse(localStorage.getItem(PROGRESS_KEY) || '{}'); } catch { return {}; }
  }, [PROGRESS_KEY]);

  const decks = config.data.flashcards;
  const [progress, setProgress] = useState(loadProgress);
  const [activeDeckId, setActiveDeckId] = useState(null);
  const [cards, setCards] = useState([]);
  const [cursor, setCursor] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [reviewOnly, setReviewOnly] = useState(false);
  // AI panel for current card: { kind: 'deepen'|'mnemonic'|null, text, status: 'idle'|'streaming'|'done'|'error', error? }
  const [aiPanel, setAiPanel] = useState({ kind: null, text: '', status: 'idle' });
  const aiAbortRef = useRef(null);
  const cardRef = useRef(null);

  const totalCards = useMemo(
    () => Object.values(decks).reduce((acc, d) => acc + d.cards.length, 0),
    [decks]
  );

  const updateProgress = useCallback((updater) => {
    setProgress((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      try { localStorage.setItem(PROGRESS_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }, [PROGRESS_KEY]);

  const openDeck = (id) => {
    const deck = decks[id];
    if (!deck) return;
    setActiveDeckId(id);
    setReviewOnly(false);
    setCards(deck.cards.map((c, idx) => ({ _idx: idx, ...c })));
    setCursor(0);
    setFlipped(false);
  };

  const backToDecks = () => {
    setActiveDeckId(null);
    setCards([]);
    setCursor(0);
    setFlipped(false);
  };

  const next = () => {
    setFlipped(false);
    setCursor((c) => (cards.length ? (c + 1) % cards.length : 0));
  };
  const prev = () => {
    setFlipped(false);
    setCursor((c) => (cards.length ? (c - 1 + cards.length) % cards.length : 0));
  };

  const shuffle = () => {
    const a = cards.slice();
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    setCards(a);
    setCursor(0);
    setFlipped(false);
  };

  const tag = (status) => {
    if (!cards.length || !activeDeckId) return;
    const card = cards[cursor];
    const k = cardKey(activeDeckId, card._idx);
    updateProgress((prev) => {
      const nextP = { ...prev };
      if (nextP[k] === status) delete nextP[k];
      else nextP[k] = status;
      return nextP;
    });
  };

  const toggleReview = () => {
    if (!activeDeckId) return;
    const deck = decks[activeDeckId];
    setReviewOnly((wasReview) => {
      const nowReview = !wasReview;
      if (nowReview) {
        setCards(
          deck.cards
            .map((c, idx) => ({ _idx: idx, ...c }))
            .filter((c) => progress[cardKey(activeDeckId, c._idx)] === 'review')
        );
      } else {
        setCards(deck.cards.map((c, idx) => ({ _idx: idx, ...c })));
      }
      setCursor(0);
      setFlipped(false);
      return nowReview;
    });
  };

  const resetAll = () => {
    if (!confirm('Reset ALL flashcard progress across every deck?')) return;
    updateProgress({});
  };

  // ---------- AI: DEEPEN / MNEMONIC (Phase 3) ----------
  // Reset visible panel + abort any in-flight stream when card or deck changes.
  useEffect(() => {
    aiAbortRef.current = true;
    setAiPanel({ kind: null, text: '', status: 'idle' });
  }, [activeDeckId, cursor]);

  const runAi = useCallback((kind, card) => {
    if (!card || !activeDeckId) return;
    // Toggle close if same panel is open in a settled state
    if (aiPanel.kind === kind && (aiPanel.status === 'done' || aiPanel.status === 'error')) {
      setAiPanel({ kind: null, text: '', status: 'idle' });
      return;
    }
    // Ignore re-clicks while streaming
    if (aiPanel.status === 'streaming') return;

    const key = aiCacheKey(kind, activeDeckId, card._idx);
    const cached = loadJSON(key);
    if (cached && typeof cached.text === 'string' && cached.text.length > 0) {
      setAiPanel({ kind, text: cached.text, status: 'done' });
      return;
    }

    if (!AI.isConfigured()) {
      setAiPanel({ kind, text: '', status: 'error', error: 'unconfigured' });
      return;
    }

    const term = card.front || '';
    const definition = card.back || '';
    let system, prompt;
    if (kind === 'deepen') {
      system = `You are an exam tutor for ${config.title}. Explain the term in exactly 3 short paragraphs:
1. What it is and why it matters on the exam.
2. How it works in practice — include one concrete real-world example.
3. The single most common mistake or confusion students have with this term.
Use plain text. No markdown. Under 220 words.`;
      prompt = `Term: ${term}\nWorking definition (from deck): ${definition}`;
    } else {
      system = `You write memory hooks for exam study. Output ONE concise mnemonic — an acronym, rhyme, or single-sentence vivid image — that helps the student recall the term. No preamble, no explanation, no quotation marks. Plain text, under 30 words.`;
      prompt = `Term: ${term}\nDefinition: ${definition}`;
    }

    setAiPanel({ kind, text: '', status: 'streaming' });
    aiAbortRef.current = false;
    let latest = '';
    const currentDeck = activeDeckId;
    const currentIdx = card._idx;

    AI.streamInto(
      (text) => {
        if (aiAbortRef.current) return;
        latest = text;
        setAiPanel((prev) =>
          prev.kind === kind && prev.status === 'streaming'
            ? { kind, text, status: 'streaming' }
            : prev
        );
      },
      {
        system,
        prompt,
        onDone: () => {
          if (aiAbortRef.current) return;
          const finalText = latest;
          setAiPanel((prev) =>
            prev.kind === kind && prev.status === 'streaming'
              ? { kind, text: finalText, status: 'done' }
              : prev
          );
          if (finalText) {
            saveJSON(aiCacheKey(kind, currentDeck, currentIdx), { text: finalText, ts: Date.now() });
          }
        },
        onError: (err) => {
          if (aiAbortRef.current) return;
          setAiPanel((prev) =>
            prev.kind === kind && prev.status === 'streaming'
              ? { kind, text: '', status: 'error', error: err?.message || 'AI request failed' }
              : prev
          );
        },
      }
    );
  }, [aiPanel.kind, aiPanel.status, activeDeckId, aiCacheKey, config.title]);

  useEffect(() => {
    if (!activeDeckId) return undefined;
    const handler = (e) => {
      if (e.target.matches?.('input, textarea, select')) return;
      switch (e.key) {
        case 'ArrowRight': e.preventDefault(); next(); break;
        case 'ArrowLeft':  e.preventDefault(); prev(); break;
        case ' ':          if (e.target !== cardRef.current) { e.preventDefault(); setFlipped((f) => !f); } break;
        case '1': tag('review'); break;
        case '2': tag('got'); break;
        case 'Escape': backToDecks(); break;
        default: break;
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  });

  if (!activeDeckId) {
    return (
      <HubLayout config={config}>
        <section>
          <div className="section-head">
            <h2>Select Deck</h2>
            <span className="section-tag">{Object.keys(decks).length} domains · {totalCards} cards</span>
          </div>
          <p>
            Pick a deck to begin. Each deck contains the core terminology for one {config.code} domain. Use
            arrow keys to navigate, space to flip, and tag cards as <strong>got it</strong> or
            <strong> review</strong>. Progress saves automatically.
          </p>

          <div className="qlink-grid">
            {Object.values(decks).map((d) => {
              const total = d.cards.length;
              let got = 0, review = 0;
              for (let i = 0; i < total; i++) {
                const v = progress[cardKey(d.id, i)];
                if (v === 'got') got++;
                else if (v === 'review') review++;
              }
              const pct = total ? Math.round((got / total) * 100) : 0;
              return (
                <a
                  key={d.id}
                  href="#"
                  className="qlink"
                  onClick={(e) => { e.preventDefault(); openDeck(d.id); }}
                >
                  <div className="qlink-id">{d.id.toUpperCase()} · {d.weight}</div>
                  <h4>{d.title}</h4>
                  <span className="desc">{total} cards · {got} got it · {review} review</span>
                  <div className="deck-mini-bar"><span style={{ width: `${pct}%` }}></span></div>
                  <span className="arrow">→</span>
                </a>
              );
            })}
          </div>

          <div className="hero-cta" style={{ marginTop: '1.5rem' }}>
            <button type="button" className="btn-mini" onClick={resetAll}>Reset all flashcard progress</button>
          </div>
        </section>
      </HubLayout>
    );
  }

  const deck = decks[activeDeckId];
  const card = cards[cursor];
  const status = card ? (progress[cardKey(activeDeckId, card._idx)] || 'untagged') : 'untagged';

  let got = 0, review = 0, untagged = 0;
  for (let i = 0; i < deck.cards.length; i++) {
    const v = progress[cardKey(activeDeckId, i)];
    if (v === 'got') got++;
    else if (v === 'review') review++;
    else untagged++;
  }

  return (
    <HubLayout config={config}>
      <section>
        <div className="section-head">
          <h2>{deck.title}</h2>
          <span className="section-tag">{cards.length ? cursor + 1 : 0} / {cards.length}</span>
        </div>

        <div className="flash-toolbar">
          <button type="button" className="btn-mini" onClick={backToDecks}>← Decks</button>
          <button type="button" className="btn-mini" onClick={shuffle}>Shuffle</button>
          <button type="button" className="btn-mini" onClick={toggleReview}>
            {reviewOnly ? 'Show all cards' : 'Show review only'}
          </button>
          <span className="flash-progress">{cards.length ? cursor + 1 : 0} / {cards.length}</span>
        </div>

        <div className="flash-stage">
          <button type="button" className="flash-arrow flash-arrow-prev" onClick={prev} aria-label="Previous card">‹</button>
          <div className="flash-card-wrap">
            <div
              ref={cardRef}
              className={`flash-card${flipped ? ' flipped' : ''}`}
              data-status={status}
              tabIndex={0}
              role="button"
              aria-label="Flip card"
              onClick={() => setFlipped((f) => !f)}
              onKeyDown={(e) => {
                if (e.key === ' ' || e.key === 'Enter') {
                  e.preventDefault();
                  setFlipped((f) => !f);
                }
              }}
            >
              <div className="flash-face flash-front">
                <div className="flash-tag">FRONT · TERM</div>
                <div className="flash-content">{card ? card.front : 'No cards in this view.'}</div>
                <div className="flash-hint">space / click to flip</div>
              </div>
              <div className="flash-face flash-back">
                <div className="flash-tag">BACK · DEFINITION</div>
                <div className="flash-content">{card ? card.back : 'Switch off "review only" or pick another deck.'}</div>
                <div className="flash-hint">space / click to flip back</div>
              </div>
            </div>
          </div>
          <button type="button" className="flash-arrow flash-arrow-next" onClick={next} aria-label="Next card">›</button>
        </div>

        <div className="flash-actions">
          <button type="button" className="btn flash-btn-review" onClick={() => tag('review')}>⚑ Review later</button>
          <button type="button" className="btn flash-btn-got" onClick={() => tag('got')}>✓ Got it</button>
        </div>

        {/* Phase 3: AI deepen / mnemonic */}
        {card && (
          <div className="flash-ai">
            <div className="flash-ai__bar">
              <button
                type="button"
                className={`flash-ai__btn${aiPanel.kind === 'deepen' && aiPanel.status !== 'idle' ? ' is-active' : ''}`}
                onClick={() => runAi('deepen', card)}
                disabled={aiPanel.status === 'streaming' && aiPanel.kind !== 'deepen'}
              >
                <Sparkles size={12} strokeWidth={2} />
                {aiPanel.kind === 'deepen' && aiPanel.status === 'streaming'
                  ? 'DEEPENING…'
                  : aiPanel.kind === 'deepen' && (aiPanel.status === 'done' || aiPanel.status === 'error')
                    ? 'HIDE'
                    : 'AI DEEPEN'}
              </button>
              <button
                type="button"
                className={`flash-ai__btn${aiPanel.kind === 'mnemonic' && aiPanel.status !== 'idle' ? ' is-active' : ''}`}
                onClick={() => runAi('mnemonic', card)}
                disabled={aiPanel.status === 'streaming' && aiPanel.kind !== 'mnemonic'}
              >
                <Lightbulb size={12} strokeWidth={2} />
                {aiPanel.kind === 'mnemonic' && aiPanel.status === 'streaming'
                  ? 'GENERATING…'
                  : aiPanel.kind === 'mnemonic' && (aiPanel.status === 'done' || aiPanel.status === 'error')
                    ? 'HIDE'
                    : 'AI MNEMONIC'}
              </button>
            </div>

            {aiPanel.kind && aiPanel.status !== 'idle' && (
              <div className={`flash-ai__body flash-ai__body--${aiPanel.kind}${aiPanel.status === 'error' ? ' flash-ai__body--err' : ''}`}>
                <div className="flash-ai__label">
                  // {aiPanel.kind === 'deepen' ? 'AI DEEPEN' : 'AI MNEMONIC'}
                </div>
                {aiPanel.status === 'error' ? (
                  aiPanel.error === 'unconfigured' ? (
                    <div>
                      AI not configured.
                      <Link to="/ai-settings" className="flash-ai__link"> CONFIGURE AI →</Link>
                    </div>
                  ) : (
                    <div>Error: {aiPanel.error || 'AI request failed'}</div>
                  )
                ) : (
                  <div className="flash-ai__text">
                    {aiPanel.text}
                    {aiPanel.status === 'streaming' && <span className="flash-ai__cursor" />}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="flash-stats">
          <span><strong>{deck.cards.length}</strong>&nbsp;total</span>
          <span><strong style={{ color: 'var(--jade)' }}>{got}</strong>&nbsp;got it</span>
          <span><strong style={{ color: 'var(--amber)' }}>{review}</strong>&nbsp;review</span>
          <span><strong style={{ color: 'var(--bone-faint)' }}>{untagged}</strong>&nbsp;untagged</span>
        </div>
      </section>
    </HubLayout>
  );
}
